import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Maximize2, Sparkles, ArrowLeft } from "lucide-react";
import { ChannelList } from "./ChannelList";
import { Conversation } from "./Conversation";
import { MusePanel } from "./MusePanel";
import { ProjectPanel } from "./ProjectPanel";
import { HubDialogs } from "./ChatDialogs";
import { FavoritesPanel } from "./FavoritesPanel";
import { Splitter } from "./Splitter";
import { useChatHub } from "./useChatHub";
import { useSplitter } from "../../lib/useSplitter";
import { MUSE_ID } from "../../lib/chat";

const parseChatPath = (p) => { const m = /^\/chat(?:\/(c|m)\/([^/?#]+))?\/?(?:[?#].*)?$/.exec(p || ""); if (!m) return null; return m[1] === "m" ? { messageId: m[2] } : m[1] === "c" ? { channelId: m[2] } : {}; };

/* 悬浮沟通窗「聊天」页签：微信式 会话列表 + 对话；项目面板为头部按钮点开的侧滑抽屉；/chat 永久链接在窗内跳转，其它链接走路由 */
export const ChatDockPane = ({ api, user, zh, mobile, openReq, onExpand, onActive }) => {
  const nav = useNavigate();
  const T = (z, e) => (zh ? z : e);
  const [target, setTarget] = useState({});
  const [side, setSide] = useState(false);
  const [list, setList] = useState(true);
  const route = useCallback((t) => setTarget(t || {}), []);
  const goTo = useCallback((p) => { const t = parseChatPath(p); if (t) setTarget(t); else nav(p); }, [nav]);
  const hub = useChatHub({ api, user, zh, channelId: target.channelId, messageId: target.messageId, route, goTo });
  const sp = useSplitter("chat.dockSplitW", 220);   // 悬浮窗左栏宽度 180–480 · 记忆 · 双击复位
  useEffect(() => { onActive?.(hub.active && hub.active !== MUSE_ID ? hub.active : null); }, [hub.active]); // eslint-disable-line
  useEffect(() => { if (openReq?.channelId || openReq?.messageId) { setTarget({ channelId: openReq.channelId, messageId: openReq.messageId }); setList(false); } }, [openReq]);
  useEffect(() => { setSide(false); }, [hub.active]);
  const pick = (id) => { hub.pick(id); setList(false); };
  const trailing = <button type="button" data-testid="comm-chat-expand" title={T("展开到完整聊天页", "Open full page")} onClick={() => onExpand(hub.active ? `/chat/c/${hub.active}` : "/chat")} className="h-8 w-8 rounded-xl flex items-center justify-center text-slate-500 hover:bg-slate-100 hover:text-[#4F6BFF]"><Maximize2 className="h-4 w-4" /></button>;
  const showList = !mobile || list;
  const showMain = !mobile || !list;
  return (
    <div className="flex h-full min-h-0 w-full" data-testid="comm-chat-pane" data-active={hub.active || ""} data-live={hub.live ? 1 : 0} data-view={showList && showMain ? "split" : showList ? "list" : "chat"}>
      {showList && <aside style={mobile ? undefined : { width: sp.w }} className={`${mobile ? "w-full" : "border-r border-slate-200/80"} shrink-0 min-h-0`} data-testid="comm-chat-list" data-width={mobile ? "" : sp.w}>
        <ChannelList items={hub.chans.items} active={hub.active} onPick={pick} onNew={() => hub.setNewCh(true)} musePending={hub.chans.musePending} zh={zh} me={hub.chans.me} directory={hub.directory} onDm={async (un) => { const ch = await hub.startDm(un); if (ch) setList(false); }} favCount={hub.favs.items.length} />
      </aside>}
      {showList && showMain && <Splitter sp={sp} zh={zh} testId="comm-chat-splitter" className="flex" />}
      {showMain && <main className="flex-1 min-w-0 flex flex-col relative bg-white">
        {hub.isFav ? (
          <FavoritesPanel api={api} hub={hub} zh={zh} compact onMenu={mobile ? () => setList(true) : undefined} trailing={trailing} />
        ) : hub.isMuse ? (
          <>
            <div className="border-b border-slate-200/80 px-2 py-1 flex items-center gap-1">
              {mobile && <button type="button" data-testid="comm-chat-back" onClick={() => setList(true)} className="h-8 w-8 rounded-xl flex items-center justify-center text-slate-500 hover:bg-slate-100"><ArrowLeft className="h-4 w-4" /></button>}
              <span className="text-xs text-slate-500 flex-1 inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-[#7c5cff]" />✦ Muse</span>{trailing}
            </div>
            <div className="flex-1 min-h-0"><MusePanel api={api} zh={zh} onNav={hub.goPermalink} onChanged={hub.onMuseChanged} /></div>
          </>
        ) : <Conversation hub={hub} api={api} user={user} zh={zh} compact onMenu={() => setList(true)} onSide={() => setSide((v) => !v)} trailing={trailing} />}
        {side && !hub.isMuse && !hub.isFav && (
          <>
            <div className="absolute inset-0 z-20 bg-slate-900/20" onClick={() => setSide(false)} data-testid="comm-side-overlay" />
            <div className="absolute inset-y-0 right-0 z-30 w-[280px] max-w-full bg-white border-l border-slate-200 shadow-2xl" data-testid="comm-side-drawer">
              <ProjectPanel api={api} channel={hub.detail} msgs={hub.msgs} zh={zh} museStats={hub.museStats} refreshKey={hub.refreshKey} onOpenProject={(pid, asCh) => { hub.openProject(pid, asCh); setSide(false); }} onNav={(u) => { hub.goPermalink(u); setSide(false); }} onMuse={() => { pick(MUSE_ID); setSide(false); }} onChanged={hub.refreshChannel} members={hub.members} me={user} />
            </div>
          </>
        )}
      </main>}
      <HubDialogs hub={hub} api={api} user={user} zh={zh} elevated />
    </div>
  );
};
